import { createContext, use, useEffect, useRef, useState } from "react";

import { Spinner } from "../spinner/spinner.tsx";

import type { ReactElement, ReactNode } from "react";

export type CollectionStatus = "loading" | "refreshing" | "ready";

export interface ShowTiming {
  readonly delayMs: number;
  readonly minMs: number;
}

const veilTiming: ShowTiming = { delayMs: 150, minMs: 300 };

export const useIsShownAfterDelay = (isOn: boolean, { delayMs, minMs }: ShowTiming): boolean => {
  const [isShown, setIsShown] = useState(false);
  const shownAtRef = useRef(0);
  useEffect(() => {
    const wait = isOn ? delayMs : Math.max(0, shownAtRef.current + minMs - performance.now());
    const timer =
      isOn === isShown
        ? undefined
        : setTimeout(() => {
            shownAtRef.current = performance.now();
            setIsShown(isOn);
          }, wait);
    return () => {
      clearTimeout(timer);
    };
  }, [isOn, isShown, delayMs, minMs]);
  return isShown;
};

const RegionLabelContext = createContext<string | undefined>(undefined);
RegionLabelContext.displayName = "RegionLabelContext";

export interface RegionLabelProviderProps {
  readonly label: string;
  readonly children: ReactNode;
}

export const RegionLabelProvider = ({ label, children }: RegionLabelProviderProps): ReactElement => (
  <RegionLabelContext value={label}>{children}</RegionLabelContext>
);

export interface RegionProps {
  readonly isBusy: boolean;
  readonly label?: string;
  readonly children: ReactNode;
}

export const Region = ({ isBusy, label, children }: RegionProps): ReactElement => {
  const isVeiled = useIsShownAfterDelay(isBusy, veilTiming);
  const announced = label ?? use(RegionLabelContext);
  return (
    <div aria-busy={isVeiled} className="relative min-w-0">
      {children}
      <div data-veiled={isVeiled ? "" : undefined} className="veil absolute inset-0 z-20 flex justify-center">
        <span
          {...(isVeiled && { role: "status" })}
          className="sticky top-6 mt-6 flex items-center self-start rounded-full border border-line bg-surface p-2 text-ink-2 shadow-float"
        >
          {isVeiled && <Spinner size={16} />}
          {isVeiled && announced !== undefined && <span className="sr-only">{announced}</span>}
        </span>
      </div>
    </div>
  );
};
